/**
 * Load-time 3D Voronoi fracture of an axis-aligned box.
 * Pure geometry — no physics, no three.js imports.
 *
 * Each cell is the box clipped by the perpendicular-bisector half-spaces
 * toward every other seed (keep the side containing the own seed).
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface Aabb {
  min: Vec3;
  max: Vec3;
}

export interface FractureCell {
  id: number;
  vertices: number[][];
  faces: number[][];
  centroid: Vec3;
  volume: number;
  aabb: Aabb;
  seed: Vec3;
  /** Parallel to faces: true if the face lies on the original box surface. */
  outerFaceFlags: boolean[];
  /** Cell ids that share a non-degenerate bisector face. */
  neighbors: number[];
}

export type Rng = () => number;

/** Deterministic mulberry32 PRNG. */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Default seed for wall fracture (stable across reloads). */
export const FRACTURE_RNG_SEED = 0xd3a01ab1;

const EPS = 1e-9;
const AREA_EPS = 1e-10;
const OUTER_PLANE_EPS = 1e-5;

// ---------------------------------------------------------------------------
// Seeds
// ---------------------------------------------------------------------------

/**
 * Sample positive spacings from a lognormal-ish distribution, then normalize
 * so they exactly fill `extent`. Biased toward a mix of small and large cells
 * (power-law-ish concrete break).
 *
 * If `targetCount` is set, draws exactly that many spacings (preferred for
 * stable cell counts). Otherwise keeps adding until sum ≥ extent.
 */
export function sampleAxisSpacings(
  extent: number,
  minSpacing: number,
  maxSpacing: number,
  rng: Rng,
  targetCount?: number,
): number[] {
  if (extent <= 0) return [];
  const lo = Math.max(minSpacing, 1e-4);
  const hi = Math.max(maxSpacing, lo * 1.01);
  const logMin = Math.log(lo);
  const logMax = Math.log(hi);

  const drawOne = (): number => {
    // u^0.9 → slight bias toward larger slabs; still plenty of small shards.
    const t = Math.pow(rng(), 0.9);
    return Math.exp(logMin + (logMax - logMin) * t);
  };

  const raw: number[] = [];
  if (targetCount != null && targetCount > 0) {
    const n = Math.max(1, Math.min(48, Math.floor(targetCount)));
    for (let i = 0; i < n; i += 1) raw.push(drawOne());
  } else {
    let sum = 0;
    while (sum < extent && raw.length < 48) {
      const s = drawOne();
      raw.push(s);
      sum += s;
    }
  }
  if (raw.length === 0) return [extent];
  const sum = raw.reduce((a, b) => a + b, 0);
  const scale = extent / sum;
  return raw.map((s) => s * scale);
}

function centersFromSpacings(spacings: number[], start: number): number[] {
  const centers: number[] = [];
  let pos = start;
  for (const s of spacings) {
    centers.push(pos + s * 0.5);
    pos += s;
  }
  return centers;
}

export interface NonUniformSeedOpts {
  /** Min face cell size (m). Default 0.28. */
  minSpacing?: number;
  /** Max face cell size (m). Default 1.2. */
  maxSpacing?: number;
  /** Jitter as fraction of local spacing (default 0.4). */
  jitter?: number;
  /** Through-thickness layers (default 2). */
  zLayers?: number;
}

/**
 * Non-uniform jittered grid: variable line spacings on X/Y (slab-to-shard),
 * fixed equal layers through Z. Deterministic via `rng`.
 * For a 6×4.2×0.6 wall → typically ~110–150 cells.
 */
export function nonUniformGridSeeds(
  halfExtents: Vec3,
  opts: NonUniformSeedOpts,
  rng: Rng,
): Vec3[] {
  const minS = opts.minSpacing ?? 0.28;
  const maxS = opts.maxSpacing ?? 1.2;
  const jitter = opts.jitter ?? 0.4;
  const zLayers = Math.max(1, opts.zLayers ?? 2);

  const extentX = 2 * halfExtents.x;
  const extentY = 2 * halfExtents.y;
  const extentZ = 2 * halfExtents.z;

  // Target line counts from geometric-mean spacing so total cells stay in band.
  // mid≈0.58 → ~10 × ~7 × 2 ≈ 140.
  const mid = Math.sqrt(minS * maxS);
  const nx = clamp(Math.round(extentX / mid), 7, 12);
  const ny = clamp(Math.round(extentY / mid), 5, 10);

  const spX = sampleAxisSpacings(extentX, minS, maxS, rng, nx);
  const spY = sampleAxisSpacings(extentY, minS, maxS, rng, ny);
  const spZ = Array.from({ length: zLayers }, () => extentZ / zLayers);

  const centersX = centersFromSpacings(spX, -halfExtents.x);
  const centersY = centersFromSpacings(spY, -halfExtents.y);
  const centersZ = centersFromSpacings(spZ, -halfExtents.z);

  const seeds: Vec3[] = [];
  for (let iz = 0; iz < centersZ.length; iz += 1) {
    for (let iy = 0; iy < centersY.length; iy += 1) {
      for (let ix = 0; ix < centersX.length; ix += 1) {
        const jx = (jitter * spX[ix]) / 2;
        const jy = (jitter * spY[iy]) / 2;
        const jz = (jitter * spZ[iz]) / 2;
        const ox = (rng() * 2 - 1) * jx;
        const oy = (rng() * 2 - 1) * jy;
        const oz = (rng() * 2 - 1) * jz;
        seeds.push({
          x: clamp(centersX[ix] + ox, -halfExtents.x + 1e-4, halfExtents.x - 1e-4),
          y: clamp(centersY[iy] + oy, -halfExtents.y + 1e-4, halfExtents.y - 1e-4),
          z: clamp(centersZ[iz] + oz, -halfExtents.z + 1e-4, halfExtents.z - 1e-4),
        });
      }
    }
  }
  return seeds;
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

// ---------------------------------------------------------------------------
// Brick masonry / anisotropic / radial shatter seeds
// ---------------------------------------------------------------------------

export interface BrickCourseSeedOpts {
  /** Course (brick) height in metres. Default 0.21. */
  courseHeight?: number;
  /** Nominal brick length along X. Default 0.45. */
  brickLength?: number;
  /** Tiny seed jitter so mortar lines read hand-laid (~0.01). */
  jitter?: number;
}

/**
 * Running-bond masonry seeds: horizontal courses at exact course height,
 * vertical cuts staggered per course (offset half brick on odd rows),
 * single layer through thickness. Seeds sit at brick centers; Voronoi
 * bisectors fall near the mortar joints.
 */
export function brickCourseSeeds(
  halfExtents: Vec3,
  opts: BrickCourseSeedOpts,
  rng: Rng,
): Vec3[] {
  const courseH = opts.courseHeight ?? 0.21;
  const brickL = opts.brickLength ?? 0.45;
  const jitter = opts.jitter ?? 0.01;

  const extentY = 2 * halfExtents.y;
  const nCourses = Math.max(1, Math.round(extentY / courseH));
  const actualCourseH = extentY / nCourses;

  const seeds: Vec3[] = [];
  for (let iy = 0; iy < nCourses; iy += 1) {
    const stagger = (iy % 2) * (brickL * 0.5);
    // Place bricks so the course fills X exactly (last brick absorbs remainder).
    const positions: number[] = [];
    let x = -halfExtents.x + stagger;
    // Leading partial brick when staggered.
    if (stagger > 1e-6) {
      positions.push(-halfExtents.x + stagger * 0.5);
    }
    while (x + brickL * 0.5 < halfExtents.x - 1e-4) {
      const cx = x + brickL * 0.5;
      if (cx < halfExtents.x - 1e-4) positions.push(cx);
      x += brickL;
    }
    // Trailing partial / last full to cover right edge.
    const lastCx = (x + halfExtents.x) * 0.5;
    if (
      positions.length === 0 ||
      Math.abs(positions[positions.length - 1] - lastCx) > brickL * 0.25
    ) {
      if (lastCx > -halfExtents.x && lastCx < halfExtents.x) {
        positions.push(clamp(lastCx, -halfExtents.x + 1e-4, halfExtents.x - 1e-4));
      }
    }
    // Ensure rightmost coverage: if gap to +half.x, add a seed.
    if (positions.length > 0) {
      const right = positions[positions.length - 1];
      if (halfExtents.x - right > brickL * 0.35) {
        positions.push(halfExtents.x - Math.min(brickL, halfExtents.x - right) * 0.5);
      }
    }

    const cy = -halfExtents.y + (iy + 0.5) * actualCourseH;
    for (const cx of positions) {
      const ox = (rng() * 2 - 1) * jitter;
      const oy = (rng() * 2 - 1) * jitter * 0.5;
      seeds.push({
        x: clamp(cx + ox, -halfExtents.x + 1e-4, halfExtents.x - 1e-4),
        y: clamp(cy + oy, -halfExtents.y + 1e-4, halfExtents.y - 1e-4),
        z: 0,
      });
    }
  }
  return seeds;
}

/**
 * Direct rectangular masonry partition (exact course planes + staggered
 * vertical cuts). Prefer this over Voronoi for true coursed-brick geometry;
 * still returns FractureCell[] with adjacency / outer flags / volume.
 */
export function fractureBoxBrickCourses(
  halfExtents: Vec3,
  opts: BrickCourseSeedOpts & { seed?: number } = {},
): FractureCell[] {
  const courseH = opts.courseHeight ?? 0.21;
  const brickL = opts.brickLength ?? 0.45;
  // jitter reserved for seed-based path; direct AABB partition stays exact
  // so volume closes and mortar lines stay axis-aligned (hand-laid via stagger).
  void opts.jitter;
  void opts.seed;

  const extentY = 2 * halfExtents.y;
  const nCourses = Math.max(1, Math.round(extentY / courseH));
  const actualCourseH = extentY / nCourses;

  type BrickRect = { minX: number; maxX: number; minY: number; maxY: number };
  const rects: BrickRect[] = [];

  for (let iy = 0; iy < nCourses; iy += 1) {
    const minY = -halfExtents.y + iy * actualCourseH;
    const maxY = minY + actualCourseH;
    const stagger = (iy % 2) * (brickL * 0.5);

    // Cut positions along X (including walls). Exact tiling → volume closure.
    const cuts: number[] = [-halfExtents.x];
    if (stagger > 1e-6) cuts.push(-halfExtents.x + stagger);
    let x = -halfExtents.x + stagger;
    while (x + brickL < halfExtents.x - 1e-5) {
      x += brickL;
      cuts.push(x);
    }
    cuts.push(halfExtents.x);
    // Dedup / sort.
    cuts.sort((a, b) => a - b);
    const unique: number[] = [];
    for (const c of cuts) {
      if (unique.length === 0 || Math.abs(c - unique[unique.length - 1]) > 1e-5) {
        unique.push(c);
      }
    }
    for (let i = 0; i < unique.length - 1; i += 1) {
      const minX = unique[i];
      const maxX = unique[i + 1];
      if (maxX - minX < 1e-4) continue;
      rects.push({ minX, maxX, minY, maxY });
    }
  }

  const minZ = -halfExtents.z;
  const maxZ = halfExtents.z;
  const cells: FractureCell[] = [];

  for (let i = 0; i < rects.length; i += 1) {
    const r = rects[i];
    const poly = makeAabbPolyhedron(r.minX, r.maxX, r.minY, r.maxY, minZ, maxZ);
    ensureOutwardWinding(poly);
    const volume = computeVolume(poly.vertices, poly.faces);
    if (volume < AREA_EPS) continue;
    const centroid = computeCentroid(poly.vertices, poly.faces, volume);
    const aabb = computeAabb(poly.vertices);
    const outerFaceFlags = markOuterFaces(poly.vertices, poly.faces, halfExtents);
    cells.push({
      id: i,
      vertices: poly.vertices,
      faces: poly.faces,
      centroid,
      volume,
      aabb,
      seed: { ...centroid },
      outerFaceFlags,
      neighbors: [],
    });
  }

  // Face-adjacency: bricks sharing a full face (or substantial contact).
  const CONTACT_EPS = 1e-3;
  const AREA_MIN = 1e-5;
  for (let i = 0; i < cells.length; i += 1) {
    for (let j = i + 1; j < cells.length; j += 1) {
      const a = cells[i].aabb;
      const b = cells[j].aabb;
      const overlapX = Math.min(a.max.x, b.max.x) - Math.max(a.min.x, b.min.x);
      const overlapY = Math.min(a.max.y, b.max.y) - Math.max(a.min.y, b.min.y);
      const overlapZ = Math.min(a.max.z, b.max.z) - Math.max(a.min.z, b.min.z);
      // Shared face if two axes overlap with area and one is coplanar.
      const touchX =
        Math.abs(a.max.x - b.min.x) < CONTACT_EPS || Math.abs(b.max.x - a.min.x) < CONTACT_EPS;
      const touchY =
        Math.abs(a.max.y - b.min.y) < CONTACT_EPS || Math.abs(b.max.y - a.min.y) < CONTACT_EPS;
      if (touchX && overlapY > CONTACT_EPS && overlapZ > CONTACT_EPS) {
        const area = overlapY * overlapZ;
        if (area > AREA_MIN) {
          cells[i].neighbors.push(cells[j].id);
          cells[j].neighbors.push(cells[i].id);
        }
      } else if (touchY && overlapX > CONTACT_EPS && overlapZ > CONTACT_EPS) {
        const area = overlapX * overlapZ;
        if (area > AREA_MIN) {
          cells[i].neighbors.push(cells[j].id);
          cells[j].neighbors.push(cells[i].id);
        }
      }
    }
  }
  for (const cell of cells) {
    cell.neighbors = Array.from(new Set(cell.neighbors)).sort((a, b) => a - b);
  }

  // Re-id densely 0..n-1 if any rect was skipped (none usually).
  return cells;
}

function makeAabbPolyhedron(
  minX: number,
  maxX: number,
  minY: number,
  maxY: number,
  minZ: number,
  maxZ: number,
): Polyhedron {
  const vertices = [
    [minX, minY, minZ],
    [maxX, minY, minZ],
    [maxX, maxY, minZ],
    [minX, maxY, minZ],
    [minX, minY, maxZ],
    [maxX, minY, maxZ],
    [maxX, maxY, maxZ],
    [minX, maxY, maxZ],
  ];
  const faces = [
    [0, 3, 2, 1], // -Z
    [4, 5, 6, 7], // +Z
    [0, 1, 5, 4], // -Y
    [3, 7, 6, 2], // +Y
    [0, 4, 7, 3], // -X
    [1, 2, 6, 5], // +X
  ];
  return { vertices, faces };
}

export interface AnisotropicSeedOpts {
  minSpacingX?: number;
  maxSpacingX?: number;
  minSpacingY?: number;
  maxSpacingY?: number;
  zLayers?: number;
  jitter?: number;
}

/**
 * Anisotropic non-uniform grid: independent X/Y spacing bands (wood grain
 * along X → large X, small Y → long thin splinter cells).
 */
export function anisotropicGridSeeds(
  halfExtents: Vec3,
  opts: AnisotropicSeedOpts,
  rng: Rng,
): Vec3[] {
  const minX = opts.minSpacingX ?? 0.9;
  const maxX = opts.maxSpacingX ?? 1.5;
  const minY = opts.minSpacingY ?? 0.12;
  const maxY = opts.maxSpacingY ?? 0.2;
  // Low jitter preserves plank aspect (high jitter round-ifies Voronoi cells).
  const jitter = opts.jitter ?? 0.12;
  const zLayers = Math.max(1, opts.zLayers ?? 1);

  const extentX = 2 * halfExtents.x;
  const extentY = 2 * halfExtents.y;
  const extentZ = 2 * halfExtents.z;

  const midX = Math.sqrt(minX * maxX);
  const midY = Math.sqrt(minY * maxY);
  // Prefer fewer X columns, more Y rows for long horizontal planks.
  const nx = clamp(Math.round(extentX / midX), 4, 8);
  const ny = clamp(Math.round(extentY / midY), 18, 42);

  const spX = sampleAxisSpacings(extentX, minX, maxX, rng, nx);
  const spY = sampleAxisSpacings(extentY, minY, maxY, rng, ny);
  const spZ = Array.from({ length: zLayers }, () => extentZ / zLayers);

  const centersX = centersFromSpacings(spX, -halfExtents.x);
  const centersY = centersFromSpacings(spY, -halfExtents.y);
  const centersZ = centersFromSpacings(spZ, -halfExtents.z);

  const seeds: Vec3[] = [];
  for (let iz = 0; iz < centersZ.length; iz += 1) {
    for (let iy = 0; iy < centersY.length; iy += 1) {
      for (let ix = 0; ix < centersX.length; ix += 1) {
        // Anisotropic jitter: less on Y so horizontal plank faces stay clean.
        const jx = (jitter * spX[ix]) / 2;
        const jy = (jitter * 0.35 * spY[iy]) / 2;
        const jz = (jitter * spZ[iz]) / 2;
        seeds.push({
          x: clamp(
            centersX[ix] + (rng() * 2 - 1) * jx,
            -halfExtents.x + 1e-4,
            halfExtents.x - 1e-4,
          ),
          y: clamp(
            centersY[iy] + (rng() * 2 - 1) * jy,
            -halfExtents.y + 1e-4,
            halfExtents.y - 1e-4,
          ),
          z: clamp(
            centersZ[iz] + (rng() * 2 - 1) * jz,
            -halfExtents.z + 1e-4,
            halfExtents.z - 1e-4,
          ),
        });
      }
    }
  }
  return seeds;
}

export function fractureBoxAnisotropic(
  halfExtents: Vec3,
  opts: AnisotropicSeedOpts & { seed?: number } = {},
): FractureCell[] {
  const seed = opts.seed ?? FRACTURE_RNG_SEED;
  const seeds = anisotropicGridSeeds(halfExtents, opts, mulberry32(seed));
  return fractureBox(halfExtents, seeds);
}

export interface RadialShatterSeedOpts {
  /** Number of concentric rings (default 6). */
  rings?: number;
  /** Nominal spoke count on the outermost ring (default 14). */
  spokes?: number;
  /** Fractional jitter of ring radius / angle (default 0.12). */
  jitter?: number;
}

/**
 * Brick-scale radial shatter: 3–6 seeds denser near a hashed impact point.
 * Jittered-grid seeds on a box yield boxy cells; this matches the wall's
 * radialShatterSeeds bias at the per-brick count.
 */
export function radialBiasedBrickSeeds(halfExtents: Vec3, count: number, rng: Rng): Vec3[] {
  const n = Math.max(3, Math.min(6, count | 0));
  const pad = 1e-4;
  const into = (x: number, y: number, z: number): Vec3 => ({
    x: clamp(x, -halfExtents.x + pad, halfExtents.x - pad),
    y: clamp(y, -halfExtents.y + pad, halfExtents.y - pad),
    z: clamp(z, -halfExtents.z + pad, halfExtents.z - pad),
  });
  const cx = (rng() * 2 - 1) * halfExtents.x * 0.55;
  const cy = (rng() * 2 - 1) * halfExtents.y * 0.42;
  const cz = (rng() * 2 - 1) * halfExtents.z * 0.32;
  const seeds: Vec3[] = [into(cx, cy, cz)];
  for (let i = 1; i < n; i += 1) {
    const theta = rng() * Math.PI * 2 + i * 0.37;
    const phi = (rng() * 2 - 1) * 1.05;
    const r = 0.28 + Math.pow(rng(), 0.38) * 1.05;
    seeds.push(
      into(
        cx + Math.cos(theta) * Math.cos(phi) * r * halfExtents.x * 1.45,
        cy + Math.sin(theta) * Math.cos(phi) * r * halfExtents.y * 1.7,
        cz + Math.sin(phi) * r * halfExtents.z * 1.35,
      ),
    );
  }
  return seeds;
}

/**
 * 2.5D radial spiderweb seeds for glass: concentric rings denser near
 * `center` (local pane space), geometric ring growth, spokes around each
 * ring, single layer through thickness. Seeds outside the pane are
 * radially clamped inward so the count stays high. Target ~90–130.
 */
export function radialShatterSeeds(
  halfExtents: Vec3,
  center: Vec3,
  opts: RadialShatterSeedOpts,
  rng: Rng,
): Vec3[] {
  const rings = Math.max(3, opts.rings ?? 6);
  const baseSpokes = Math.max(8, opts.spokes ?? 14);
  const jitter = opts.jitter ?? 0.12;

  const pad = 0.03;
  const cx = clamp(center.x, -halfExtents.x + pad, halfExtents.x - pad);
  const cy = clamp(center.y, -halfExtents.y + pad, halfExtents.y - pad);

  // Distance to farthest corner — outer rings reach the pane edge.
  const maxR = Math.hypot(
    Math.max(Math.abs(cx + halfExtents.x), Math.abs(cx - halfExtents.x)),
    Math.max(Math.abs(cy + halfExtents.y), Math.abs(cy - halfExtents.y)),
  );

  const clampIntoPane = (x: number, y: number): Vec3 => ({
    x: clamp(x, -halfExtents.x + pad, halfExtents.x - pad),
    y: clamp(y, -halfExtents.y + pad, halfExtents.y - pad),
    z: 0,
  });

  const seeds: Vec3[] = [];
  const pushSeed = (x: number, y: number, minDist = 0.045) => {
    const p = clampIntoPane(x, y);
    for (const s of seeds) {
      if (Math.hypot(s.x - p.x, s.y - p.y) < minDist) return;
    }
    seeds.push({
      ...p,
      z: clamp((rng() * 2 - 1) * halfExtents.z * 0.2, -halfExtents.z + 1e-4, halfExtents.z - 1e-4),
    });
  };

  // Center seed.
  pushSeed(cx + (rng() * 2 - 1) * 0.008, cy + (rng() * 2 - 1) * 0.008, 0.01);

  // Dense near-center rings + geometric growth outward.
  const growth = 1.38;
  const r0 = Math.min(0.12, maxR * 0.08);
  for (let ri = 0; ri < rings; ri += 1) {
    const rNom = r0 * Math.pow(growth, ri);
    if (rNom > maxR * 1.05) break;
    // More spokes outward; denser near impact.
    const t = (ri + 1) / rings;
    const spokes = Math.max(
      8,
      Math.round(baseSpokes * (0.65 + 0.7 * t) + (ri < 2 ? 4 : 0)),
    );
    const angleOff = (ri % 2) * (Math.PI / spokes) + rng() * 0.02;
    for (let s = 0; s < spokes; s += 1) {
      const ang = angleOff + (s / spokes) * Math.PI * 2 + (rng() * 2 - 1) * jitter * 0.35;
      const r = rNom * (1 + (rng() * 2 - 1) * jitter * 0.28);
      pushSeed(cx + Math.cos(ang) * r, cy + Math.sin(ang) * r, ri < 2 ? 0.03 : 0.05);
    }
  }

  // Edge / corner fill so periphery isn't one giant plate.
  const edgePad = 0.1;
  const edgeSeeds: Array<[number, number]> = [
    [-halfExtents.x + edgePad, -halfExtents.y + edgePad],
    [halfExtents.x - edgePad, -halfExtents.y + edgePad],
    [-halfExtents.x + edgePad, halfExtents.y - edgePad],
    [halfExtents.x - edgePad, halfExtents.y - edgePad],
    [0, -halfExtents.y + edgePad],
    [0, halfExtents.y - edgePad],
    [-halfExtents.x + edgePad, 0],
    [halfExtents.x - edgePad, 0],
    [-halfExtents.x + edgePad, halfExtents.y * 0.5],
    [halfExtents.x - edgePad, halfExtents.y * 0.5],
    [-halfExtents.x + edgePad, -halfExtents.y * 0.5],
    [halfExtents.x - edgePad, -halfExtents.y * 0.5],
    [halfExtents.x * 0.5, -halfExtents.y + edgePad],
    [-halfExtents.x * 0.5, -halfExtents.y + edgePad],
    [halfExtents.x * 0.5, halfExtents.y - edgePad],
    [-halfExtents.x * 0.5, halfExtents.y - edgePad],
  ];
  for (const [x, y] of edgeSeeds) {
    pushSeed(x, y, 0.1);
  }

  // If still short of target band, sprinkle a mild polar fill (cap ~118).
  let guard = 0;
  while (seeds.length < 110 && guard < 60) {
    guard += 1;
    const ang = rng() * Math.PI * 2;
    const r = (0.15 + rng() * 0.85) * maxR * 0.95;
    pushSeed(cx + Math.cos(ang) * r, cy + Math.sin(ang) * r, 0.06);
  }
  // Hard cap so Voronoi stays in the 90–130 cell band after any degenerates.
  if (seeds.length > 120) seeds.length = 120;

  return seeds;
}

export function fractureBoxRadialShatter(
  halfExtents: Vec3,
  center: Vec3,
  opts: RadialShatterSeedOpts & { seed?: number } = {},
): FractureCell[] {
  const seed = opts.seed ?? FRACTURE_RNG_SEED;
  // Mix impact position into seed so different hits differ deterministically.
  const impactSeed =
    (seed ^
      (Math.floor(center.x * 1000) * 73856093) ^
      (Math.floor(center.y * 1000) * 19349663)) >>>
    0;
  const seeds = radialShatterSeeds(halfExtents, center, opts, mulberry32(impactSeed));
  return fractureBox(halfExtents, seeds);
}

// ---------------------------------------------------------------------------
// Convex polyhedron helpers
// ---------------------------------------------------------------------------

interface Polyhedron {
  vertices: number[][];
  faces: number[][];
}

function makeBoxPolyhedron(half: Vec3): Polyhedron {
  const { x: hx, y: hy, z: hz } = half;
  const vertices = [
    [-hx, -hy, -hz],
    [hx, -hy, -hz],
    [hx, hy, -hz],
    [-hx, hy, -hz],
    [-hx, -hy, hz],
    [hx, -hy, hz],
    [hx, hy, hz],
    [-hx, hy, hz],
  ];
  // Outward-winding faces (CCW when viewed from outside / right-hand rule
  // gives outward normals). Prior ordering was inverted → hollow shells under
  // FrontSide culling and dark “pillow” seams on coplanar outer faces.
  const faces = [
    [0, 3, 2, 1], // -Z  normal (0, 0, -1)
    [4, 5, 6, 7], // +Z  normal (0, 0, +1)
    [0, 1, 5, 4], // -Y  normal (0, -1, 0)
    [3, 7, 6, 2], // +Y  normal (0, +1, 0)
    [0, 4, 7, 3], // -X  normal (-1, 0, 0)
    [1, 2, 6, 5], // +X  normal (+1, 0, 0)
  ];
  return { vertices, faces };
}

/** Plane: nx*x + ny*y + nz*z + d >= 0 is the kept half-space. */
export interface Plane {
  nx: number;
  ny: number;
  nz: number;
  d: number;
}

function signedDistance(plane: Plane, v: number[]): number {
  return plane.nx * v[0] + plane.ny * v[1] + plane.nz * v[2] + plane.d;
}

function lerpVertex(a: number[], b: number[], t: number): number[] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/**
 * Clip a convex polyhedron by a plane (Sutherland–Hodgman per face + cap).
 * Returns null if the result is empty / degenerate.
 */
export function clipPolyhedronByPlane(poly: Polyhedron, plane: Plane): Polyhedron | null {
  const nV = poly.vertices.length;
  if (nV === 0 || poly.faces.length === 0) return null;

  const sides = new Float64Array(nV);
  let keepCount = 0;
  let outCount = 0;
  for (let i = 0; i < nV; i += 1) {
    sides[i] = signedDistance(plane, poly.vertices[i]);
    if (sides[i] >= -EPS) keepCount += 1;
    else outCount += 1;
  }
  if (keepCount === 0) return null;
  if (outCount === 0) {
    return {
      vertices: poly.vertices.map((v) => v.slice()),
      faces: poly.faces.map((f) => f.slice()),
    };
  }

  const newVertices: number[][] = [];
  const vertexMap = new Map<number, number>();
  const getOrAdd = (oldIdx: number): number => {
    const existing = vertexMap.get(oldIdx);
    if (existing !== undefined) return existing;
    const idx = newVertices.length;
    newVertices.push(poly.vertices[oldIdx].slice());
    vertexMap.set(oldIdx, idx);
    return idx;
  };

  const edgeCache = new Map<string, number>();
  const intersectEdge = (i: number, j: number): number => {
    const lo = Math.min(i, j);
    const hi = Math.max(i, j);
    const key = `${lo}|${hi}`;
    const cached = edgeCache.get(key);
    if (cached !== undefined) return cached;
    const si = sides[i];
    const sj = sides[j];
    const denom = si - sj;
    const t = Math.abs(denom) < EPS ? 0.5 : si / denom;
    const p = lerpVertex(poly.vertices[i], poly.vertices[j], t);
    const idx = newVertices.length;
    newVertices.push(p);
    edgeCache.set(key, idx);
    return idx;
  };

  const newFaces: number[][] = [];
  const cutEdges: Array<[number, number]> = [];

  for (const face of poly.faces) {
    if (face.length < 3) continue;
    const m = face.length;
    const clipped: number[] = [];
    let leave = -1;
    let enter = -1;

    for (let k = 0; k < m; k += 1) {
      const curr = face[k];
      const next = face[(k + 1) % m];
      const currIn = sides[curr] >= -EPS;
      const nextIn = sides[next] >= -EPS;

      if (currIn && nextIn) {
        clipped.push(getOrAdd(curr));
      } else if (currIn && !nextIn) {
        clipped.push(getOrAdd(curr));
        const mid = intersectEdge(curr, next);
        clipped.push(mid);
        leave = mid;
      } else if (!currIn && nextIn) {
        const mid = intersectEdge(curr, next);
        clipped.push(mid);
        clipped.push(getOrAdd(next));
        enter = mid;
      }
    }

    if (leave >= 0 && enter >= 0 && leave !== enter) {
      // Clipped face walks leave→enter along the cut (see loop above). Cap must
      // use the opposite directed edge enter→leave so the undirected edge is
      // shared twice. With outward-wound input faces this also orients the cap
      // outward (plane normal points into the kept half-space = inward).
      cutEdges.push([enter, leave]);
    }

    const cleaned = dedupeLoop(clipped);
    if (cleaned.length >= 3) newFaces.push(cleaned);
  }

  const cap = chainCutEdges(cutEdges);
  if (cap && cap.length >= 3) newFaces.push(cap);

  if (newVertices.length < 4 || newFaces.length < 4) return null;
  return compactPolyhedron({ vertices: newVertices, faces: newFaces });
}

function dedupeLoop(indices: number[]): number[] {
  if (indices.length === 0) return indices;
  const out: number[] = [];
  for (let i = 0; i < indices.length; i += 1) {
    const v = indices[i];
    const prev = out.length > 0 ? out[out.length - 1] : -1;
    if (v !== prev) out.push(v);
  }
  if (out.length > 1 && out[0] === out[out.length - 1]) out.pop();
  return out;
}

/** Chain directed cut edges into a single closed polygon. */
function chainCutEdges(edges: Array<[number, number]>): number[] | null {
  if (edges.length < 3) return null;
  const nextMap = new Map<number, number>();
  for (const [a, b] of edges) nextMap.set(a, b);
  const start = edges[0][0];
  const loop: number[] = [start];
  let cur = start;
  for (let i = 0; i < edges.length + 2; i += 1) {
    const n = nextMap.get(cur);
    if (n === undefined) return null;
    if (n === start) break;
    if (loop.includes(n)) return null;
    loop.push(n);
    cur = n;
  }
  return loop.length >= 3 ? loop : null;
}

function compactPolyhedron(poly: Polyhedron): Polyhedron {
  const used = new Set<number>();
  for (const f of poly.faces) for (const i of f) used.add(i);
  const map = new Map<number, number>();
  const vertices: number[][] = [];
  // Preserve insertion order of used indices for determinism.
  const usedList = Array.from(used).sort((a, b) => a - b);
  for (const old of usedList) {
    map.set(old, vertices.length);
    vertices.push(poly.vertices[old]);
  }
  const faces = poly.faces
    .map((f) => dedupeLoop(f.map((i) => map.get(i)!)))
    .filter((f) => f.length >= 3);
  return { vertices, faces };
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

function scalarTriple(a: number[], b: number[], c: number[]): number {
  return (
    a[0] * (b[1] * c[2] - b[2] * c[1]) -
    a[1] * (b[0] * c[2] - b[2] * c[0]) +
    a[2] * (b[0] * c[1] - b[1] * c[0])
  );
}

function faceNormalArea(
  vertices: number[][],
  face: number[],
): { nx: number; ny: number; nz: number; area: number } {
  let nx = 0;
  let ny = 0;
  let nz = 0;
  const v0 = vertices[face[0]];
  for (let i = 1; i < face.length - 1; i += 1) {
    const v1 = vertices[face[i]];
    const v2 = vertices[face[i + 1]];
    nx += (v1[1] - v0[1]) * (v2[2] - v0[2]) - (v1[2] - v0[2]) * (v2[1] - v0[1]);
    ny += (v1[2] - v0[2]) * (v2[0] - v0[0]) - (v1[0] - v0[0]) * (v2[2] - v0[2]);
    nz += (v1[0] - v0[0]) * (v2[1] - v0[1]) - (v1[1] - v0[1]) * (v2[0] - v0[0]);
  }
  const len = Math.hypot(nx, ny, nz);
  if (len < EPS) return { nx: 0, ny: 0, nz: 0, area: 0 };
  return { nx: nx / len, ny: ny / len, nz: nz / len, area: len * 0.5 };
}

/** Signed volume via face fans (positive ⇒ outward winding). */
function computeSignedVolume(vertices: number[][], faces: number[][]): number {
  let vol6 = 0;
  for (const face of faces) {
    if (face.length < 3) continue;
    const v0 = vertices[face[0]];
    for (let i = 1; i < face.length - 1; i += 1) {
      vol6 += scalarTriple(v0, vertices[face[i]], vertices[face[i + 1]]);
    }
  }
  return vol6 / 6;
}

function computeVolume(vertices: number[][], faces: number[][]): number {
  return Math.abs(computeSignedVolume(vertices, faces));
}

/** Flip every face if the polyhedron is inward-wound (signed volume < 0). */
function ensureOutwardWinding(poly: Polyhedron): void {
  if (computeSignedVolume(poly.vertices, poly.faces) >= 0) return;
  for (const face of poly.faces) face.reverse();
}

function computeCentroid(vertices: number[][], faces: number[][], volume: number): Vec3 {
  if (volume < EPS || vertices.length === 0) {
    return averageVertices(vertices);
  }

  let cx = 0;
  let cy = 0;
  let cz = 0;
  let signedVol6 = 0;
  for (const face of faces) {
    if (face.length < 3) continue;
    const v0 = vertices[face[0]];
    for (let i = 1; i < face.length - 1; i += 1) {
      const v1 = vertices[face[i]];
      const v2 = vertices[face[i + 1]];
      const tetVol6 = scalarTriple(v0, v1, v2);
      signedVol6 += tetVol6;
      cx += tetVol6 * (v0[0] + v1[0] + v2[0]);
      cy += tetVol6 * (v0[1] + v1[1] + v2[1]);
      cz += tetVol6 * (v0[2] + v1[2] + v2[2]);
    }
  }
  // sum (vol_t * centroid_t) / V with centroid_t = (v0+v1+v2)/4, vol_t = tet/6
  // → cx_raw / (24 V) with V = |signedVol6|/6 → denom = 4 * signedVol6 when positive
  const denom = 4 * signedVol6;
  if (Math.abs(denom) < EPS) return averageVertices(vertices);
  return { x: cx / denom, y: cy / denom, z: cz / denom };
}

function averageVertices(vertices: number[][]): Vec3 {
  let x = 0;
  let y = 0;
  let z = 0;
  const n = Math.max(vertices.length, 1);
  for (const v of vertices) {
    x += v[0];
    y += v[1];
    z += v[2];
  }
  return { x: x / n, y: y / n, z: z / n };
}

function computeAabb(vertices: number[][]): Aabb {
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (const v of vertices) {
    if (v[0] < minX) minX = v[0];
    if (v[1] < minY) minY = v[1];
    if (v[2] < minZ) minZ = v[2];
    if (v[0] > maxX) maxX = v[0];
    if (v[1] > maxY) maxY = v[1];
    if (v[2] > maxZ) maxZ = v[2];
  }
  return {
    min: { x: minX, y: minY, z: minZ },
    max: { x: maxX, y: maxY, z: maxZ },
  };
}

function maxVertexDistFromSeed(vertices: number[][], seed: Vec3): number {
  let maxD = 0;
  for (const v of vertices) {
    const d = Math.hypot(v[0] - seed.x, v[1] - seed.y, v[2] - seed.z);
    if (d > maxD) maxD = d;
  }
  return maxD;
}

function markOuterFaces(vertices: number[][], faces: number[][], half: Vec3): boolean[] {
  const checks: Array<(v: number[]) => number> = [
    (v) => Math.abs(v[0] - half.x),
    (v) => Math.abs(v[0] + half.x),
    (v) => Math.abs(v[1] - half.y),
    (v) => Math.abs(v[1] + half.y),
    (v) => Math.abs(v[2] - half.z),
    (v) => Math.abs(v[2] + half.z),
  ];
  return faces.map((face) => {
    if (face.length < 3) return false;
    for (const dist of checks) {
      let on = true;
      for (const idx of face) {
        if (dist(vertices[idx]) > OUTER_PLANE_EPS) {
          on = false;
          break;
        }
      }
      if (on) return true;
    }
    return false;
  });
}

function faceLiesOnPlane(vertices: number[][], face: number[], plane: Plane, tol: number): boolean {
  for (const idx of face) {
    if (Math.abs(signedDistance(plane, vertices[idx])) > tol) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Fracture
// ---------------------------------------------------------------------------

/**
 * Fracture an axis-aligned box (centered at origin) into Voronoi cells.
 * `seeds` are in the same local space as the box.
 */
export function fractureBox(halfExtents: Vec3, seeds: Vec3[]): FractureCell[] {
  const n = seeds.length;
  if (n === 0) return [];

  const cells: FractureCell[] = [];
  const neighborSets: Array<Set<number>> = Array.from({ length: n }, () => new Set());

  for (let i = 0; i < n; i += 1) {
    const seed = seeds[i];
    let poly: Polyhedron | null = makeBoxPolyhedron(halfExtents);

    const others: Array<{ j: number; dist: number }> = [];
    for (let j = 0; j < n; j += 1) {
      if (j === i) continue;
      const oj = seeds[j];
      others.push({
        j,
        dist: Math.hypot(oj.x - seed.x, oj.y - seed.y, oj.z - seed.z),
      });
    }
    others.sort((a, b) => a.dist - b.dist);

    for (const { j, dist } of others) {
      if (!poly) break;
      const maxR = maxVertexDistFromSeed(poly.vertices, seed);
      if (dist * 0.5 > maxR + EPS) break;

      const other = seeds[j];
      const mx = (seed.x + other.x) * 0.5;
      const my = (seed.y + other.y) * 0.5;
      const mz = (seed.z + other.z) * 0.5;
      // Keep side containing seed: (x - m) · (other - seed) <= 0
      const dx = other.x - seed.x;
      const dy = other.y - seed.y;
      const dz = other.z - seed.z;
      const plane: Plane = {
        nx: -dx,
        ny: -dy,
        nz: -dz,
        d: dx * mx + dy * my + dz * mz,
      };

      const clipped = clipPolyhedronByPlane(poly, plane);
      if (!clipped) {
        poly = null;
        break;
      }

      // Bisector face → neighbor candidate (verify by face area).
      for (const face of clipped.faces) {
        if (!faceLiesOnPlane(clipped.vertices, face, plane, OUTER_PLANE_EPS * 20)) continue;
        const { area } = faceNormalArea(clipped.vertices, face);
        if (area > AREA_EPS) {
          neighborSets[i].add(j);
          break;
        }
      }

      poly = clipped;
    }

    if (!poly || poly.vertices.length < 4) continue;

    // Clip preserves relative winding; enforce outward so FrontSide rendering
    // and flat normals face out of each convex cell (no hollow shells).
    ensureOutwardWinding(poly);

    const volume = computeVolume(poly.vertices, poly.faces);
    if (volume < AREA_EPS) continue;

    const centroid = computeCentroid(poly.vertices, poly.faces, volume);
    const aabb = computeAabb(poly.vertices);
    const outerFaceFlags = markOuterFaces(poly.vertices, poly.faces, halfExtents);

    cells.push({
      id: i,
      vertices: poly.vertices,
      faces: poly.faces,
      centroid,
      volume,
      aabb,
      seed: { ...seed },
      outerFaceFlags,
      neighbors: [],
    });
  }

  const surviving = new Set(cells.map((c) => c.id));
  const neighborMap = new Map<number, Set<number>>();
  for (const cell of cells) {
    const set = new Set<number>();
    for (const j of neighborSets[cell.id]) {
      if (surviving.has(j)) set.add(j);
    }
    neighborMap.set(cell.id, set);
  }
  // Symmetrize.
  for (const cell of cells) {
    for (const j of neighborMap.get(cell.id) ?? []) {
      neighborMap.get(j)?.add(cell.id);
    }
  }
  for (const cell of cells) {
    cell.neighbors = Array.from(neighborMap.get(cell.id) ?? []).sort((a, b) => a - b);
  }

  return cells;
}

/** Box volume from half-extents. */
export function boxVolume(halfExtents: Vec3): number {
  return 8 * halfExtents.x * halfExtents.y * halfExtents.z;
}

/** Convenience: fracture with non-uniform spacing + fixed seed. */
export function fractureBoxNonUniform(
  halfExtents: Vec3,
  opts: NonUniformSeedOpts & { seed?: number } = {},
): FractureCell[] {
  const seed = opts.seed ?? FRACTURE_RNG_SEED;
  const seeds = nonUniformGridSeeds(halfExtents, opts, mulberry32(seed));
  return fractureBox(halfExtents, seeds);
}

// ---------------------------------------------------------------------------
// Runtime sub-fracture (one generation)
// ---------------------------------------------------------------------------

/** Point inside a convex polyhedron with outward-wound faces. */
function pointInConvexPolyhedron(poly: Polyhedron, p: Vec3, eps = 1e-6): boolean {
  for (const face of poly.faces) {
    if (face.length < 3) continue;
    const { nx, ny, nz, area } = faceNormalArea(poly.vertices, face);
    if (area < AREA_EPS) continue;
    const v0 = poly.vertices[face[0]];
    // Outward normal: inside ⇒ (p - v0) · n ≤ 0
    const side = (p.x - v0[0]) * nx + (p.y - v0[1]) * ny + (p.z - v0[2]) * nz;
    if (side > eps) return false;
  }
  return true;
}

/**
 * Rejection-sample `count` seeds inside the cell polyhedron (AABB proposal).
 * Falls back to centroid jitter if rejection fails.
 */
function sampleSeedsInCell(cell: FractureCell, count: number, rng: Rng): Vec3[] {
  const poly: Polyhedron = { vertices: cell.vertices, faces: cell.faces };
  const { min, max } = cell.aabb;
  const dx = Math.max(max.x - min.x, 1e-4);
  const dy = Math.max(max.y - min.y, 1e-4);
  const dz = Math.max(max.z - min.z, 1e-4);
  const seeds: Vec3[] = [];
  const maxAttempts = count * 40;

  for (let attempt = 0; attempt < maxAttempts && seeds.length < count; attempt += 1) {
    const p: Vec3 = {
      x: min.x + rng() * dx,
      y: min.y + rng() * dy,
      z: min.z + rng() * dz,
    };
    if (!pointInConvexPolyhedron(poly, p)) continue;
    // Reject if too close to an existing seed (degenerate bisector).
    let ok = true;
    for (const s of seeds) {
      if (Math.hypot(p.x - s.x, p.y - s.y, p.z - s.z) < 1e-3) {
        ok = false;
        break;
      }
    }
    if (ok) seeds.push(p);
  }

  // Pad with centroid-biased samples if rejection undershot.
  while (seeds.length < count) {
    const j = 0.15;
    const p: Vec3 = {
      x: clamp(cell.centroid.x + (rng() * 2 - 1) * dx * j, min.x + 1e-4, max.x - 1e-4),
      y: clamp(cell.centroid.y + (rng() * 2 - 1) * dy * j, min.y + 1e-4, max.y - 1e-4),
      z: clamp(cell.centroid.z + (rng() * 2 - 1) * dz * j, min.z + 1e-4, max.z - 1e-4),
    };
    seeds.push(p);
  }
  return seeds;
}

/**
 * Inherit outer-face flags from parent: a face is outer only if it lies on a
 * parent outer-face plane. All new cut faces are interior.
 */
function inheritOuterFaceFlags(poly: Polyhedron, parent: FractureCell): boolean[] {
  const outerPlanes: Plane[] = [];
  for (let fi = 0; fi < parent.faces.length; fi += 1) {
    if (!parent.outerFaceFlags[fi]) continue;
    const face = parent.faces[fi];
    if (face.length < 3) continue;
    const { nx, ny, nz, area } = faceNormalArea(parent.vertices, face);
    if (area < AREA_EPS) continue;
    const v0 = parent.vertices[face[0]];
    outerPlanes.push({
      nx,
      ny,
      nz,
      d: -(nx * v0[0] + ny * v0[1] + nz * v0[2]),
    });
  }

  return poly.faces.map((face) => {
    if (face.length < 3) return false;
    for (const plane of outerPlanes) {
      if (faceLiesOnPlane(poly.vertices, face, plane, OUTER_PLANE_EPS * 20)) return true;
    }
    return false;
  });
}

/**
 * Split a convex FractureCell into `count` sub-cells via Voronoi bisector
 * clipping with seeds inside the cell. Generation-1 only (caller must not
 * re-subdivide the result).
 *
 * Outer-face inheritance: faces coplanar with a parent outer face stay outer;
 * all new cut faces are interior.
 */
export function subdivideCell(cell: FractureCell, count: number, rng: Rng): FractureCell[] {
  const n = Math.max(2, Math.min(Math.floor(count), 12));
  const seeds = sampleSeedsInCell(cell, n, rng);
  if (seeds.length < 2) {
    return [
      {
        ...cell,
        vertices: cell.vertices.map((v) => v.slice()),
        faces: cell.faces.map((f) => f.slice()),
        outerFaceFlags: cell.outerFaceFlags.slice(),
        neighbors: [],
      },
    ];
  }

  const parentPoly: Polyhedron = {
    vertices: cell.vertices,
    faces: cell.faces,
  };
  const results: FractureCell[] = [];

  for (let i = 0; i < seeds.length; i += 1) {
    const seed = seeds[i];
    let poly: Polyhedron | null = {
      vertices: parentPoly.vertices.map((v) => v.slice()),
      faces: parentPoly.faces.map((f) => f.slice()),
    };

    const others: Array<{ j: number; dist: number }> = [];
    for (let j = 0; j < seeds.length; j += 1) {
      if (j === i) continue;
      const oj = seeds[j];
      others.push({
        j,
        dist: Math.hypot(oj.x - seed.x, oj.y - seed.y, oj.z - seed.z),
      });
    }
    others.sort((a, b) => a.dist - b.dist);

    for (const { j, dist } of others) {
      if (!poly) break;
      const maxR = maxVertexDistFromSeed(poly.vertices, seed);
      if (dist * 0.5 > maxR + EPS) break;

      const other = seeds[j];
      const mx = (seed.x + other.x) * 0.5;
      const my = (seed.y + other.y) * 0.5;
      const mz = (seed.z + other.z) * 0.5;
      const dx = other.x - seed.x;
      const dy = other.y - seed.y;
      const dz = other.z - seed.z;
      const plane: Plane = {
        nx: -dx,
        ny: -dy,
        nz: -dz,
        d: dx * mx + dy * my + dz * mz,
      };

      const clipped = clipPolyhedronByPlane(poly, plane);
      if (!clipped) {
        poly = null;
        break;
      }
      poly = clipped;
    }

    if (!poly || poly.vertices.length < 4) continue;
    ensureOutwardWinding(poly);

    const volume = computeVolume(poly.vertices, poly.faces);
    if (volume < AREA_EPS) continue;

    const centroid = computeCentroid(poly.vertices, poly.faces, volume);
    const aabb = computeAabb(poly.vertices);
    const outerFaceFlags = inheritOuterFaceFlags(poly, cell);

    results.push({
      id: i,
      vertices: poly.vertices,
      faces: poly.faces,
      centroid,
      volume,
      aabb,
      seed: { ...seed },
      outerFaceFlags,
      neighbors: [],
    });
  }

  return results;
}
